import React, { useState } from 'react';
import { 
  Code, 
  Copy, 
  Check, 
  ExternalLink, 
  Search,
  Filter,
  BookOpen,
  Zap,
  Shield,
  Palette,
  FileText,
  Globe,
  Image,
  Type,
  Calculator
} from 'lucide-react';
import { motion } from 'framer-motion';
import config from '../config';
import { apiPost } from '../utils/api';

const ApiDocs = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [copiedEndpoint, setCopiedEndpoint] = useState(null);
  const [testStates, setTestStates] = useState({}); // { endpoint: { open: bool, request: string, response: any, loading: bool, error: string } }

  const categories = [
    { id: 'all', name: 'Todas', icon: BookOpen, color: 'text-blue-600' },
    { id: 'utils', name: 'Utilitários', icon: Calculator, color: 'text-green-600' },
    { id: 'converters', name: 'Conversores', icon: Palette, color: 'text-purple-600' },
    { id: 'security', name: 'Segurança', icon: Shield, color: 'text-red-600' },
    { id: 'content', name: 'Conteúdo', icon: FileText, color: 'text-orange-600' },
    { id: 'domains', name: 'Domínios', icon: Globe, color: 'text-indigo-600' },
    { id: 'images', name: 'Imagens', icon: Image, color: 'text-pink-600' },
    { id: 'development', name: 'Desenvolvimento', icon: Code, color: 'text-gray-600' }
  ];

  const apiEndpoints = [
    // Utilitários
    {
      category: 'utils',
      method: 'POST',
      endpoint: '/api/utils/hash',
      title: 'Geração de Hash',
      description: 'Gera hashes MD5, SHA1, SHA256, SHA512 de um texto',
      requestBody: {
        text: 'string',
        algorithm: 'md5|sha1|sha256|sha512'
      },
      response: {
        hash: 'string',
        algorithm: 'string'
      },
      example: {
        request: {
          text: 'Hello World',
          algorithm: 'sha256'
        },
        response: {
          hash: 'a591a6d40bf420404a011733cfb7b190d62c65bf0bcda32b57b277d9ad9f146e',
          algorithm: 'sha256'
        }
      }
    },
    {
      category: 'utils',
      method: 'POST',
      endpoint: '/api/utils/base64',
      title: 'Codificação Base64',
      description: 'Codifica texto em Base64',
      requestBody: {
        text: 'string',
        action: 'encode'
      },
      response: {
        encoded: 'string'
      },
      example: {
        request: {
          text: 'Hello World',
          action: 'encode'
        },
        response: {
          encoded: 'SGVsbG8gV29ybGQ='
        }
      }
    },
    {
      category: 'utils',
      method: 'POST',
      endpoint: '/api/utils/base64',
      title: 'Decodificação Base64',
      description: 'Decodifica texto de Base64',
      requestBody: {
        encoded: 'string',
        action: 'decode'
      },
      response: {
        decoded: 'string'
      },
      example: {
        request: {
          encoded: 'SGVsbG8gV29ybGQ=',
          action: 'decode'
        },
        response: {
          decoded: 'Hello World'
        }
      }
    },
    // Conversores
    {
      category: 'converters',
      method: 'POST',
      endpoint: '/api/converters/temperature',
      title: 'Conversor de Temperatura',
      description: 'Converte valores entre Celsius, Fahrenheit e Kelvin.',
      requestBody: {
        value: 'number',
        from: 'celsius|fahrenheit|kelvin',
        to: 'celsius|fahrenheit|kelvin'
      },
      response: {
        result: 'number'
      },
      example: {
        request: { value: 25, from: 'celsius', to: 'fahrenheit' },
        response: { result: 77 }
      }
    },
    {
      category: 'converters',
      method: 'POST',
      endpoint: '/api/converters/currency-converter',
      title: 'Conversão de Moedas',
      description: 'Obtém taxas de câmbio atualizadas',
      requestBody: {
        amount: 'number',
        fromCurrency: 'string',
        toCurrency: 'string'
      },
      response: {
        original: 'number',
        result: 'string',
        fromUnit: 'string',
        toUnit: 'string',
        category: 'string'
      },
      example: {
        request: {
          amount: 100,
          fromCurrency: 'USD',
          toCurrency: 'BRL'
        },
        response: {
          original: 100,
          result: '525.00',
          fromUnit: 'USD',
          toUnit: 'BRL',
          category: 'currency'
        }
      }
    },
    // Segurança
    {
      category: 'security',
      method: 'POST',
      endpoint: '/api/security/password-strength',
      title: 'Validação de Senha',
      description: 'Analisa a força e requisitos de uma senha',
      requestBody: {
        password: 'string'
      },
      response: {
        isValid: 'boolean',
        score: 'number',
        feedback: 'array',
        requirements: 'object'
      },
      example: {
        request: {
          password: 'MyPassword123!'
        },
        response: {
          isValid: true,
          score: 85,
          feedback: ['Senha forte'],
          requirements: {
            length: true,
            uppercase: true,
            lowercase: true,
            numbers: true,
            symbols: true
          }
        }
      }
    },
    {
      category: 'security',
      method: 'POST',
      endpoint: '/api/security/email-validator',
      title: 'Validação de Email',
      description: 'Verifica o formato e validade de um email',
      requestBody: {
        email: 'string'
      },
      response: {
        isValid: 'boolean',
        format: 'boolean',
        domain: 'boolean'
      },
      example: {
        request: {
          email: 'user@example.com'
        },
        response: {
          isValid: true,
          format: true,
          domain: true
        }
      }
    },
    {
      category: 'security',
      method: 'POST',
      endpoint: '/api/security/cpf-validator',
      title: 'Validação de CPF',
      description: 'Valida CPF brasileiro com algoritmo oficial',
      requestBody: {
        cpf: 'string'
      },
      response: {
        isValid: 'boolean',
        formatted: 'string'
      },
      example: {
        request: {
          cpf: '12345678909'
        },
        response: {
          isValid: true,
          formatted: '123.456.789-09'
        }
      }
    },
    {
      category: 'security',
      method: 'POST',
      endpoint: '/api/security/cnpj-validator',
      title: 'Validação de CNPJ',
      description: 'Valida CNPJ brasileiro com algoritmo oficial',
      requestBody: {
        cnpj: 'string'
      },
      response: {
        isValid: 'boolean',
        formatted: 'string'
      },
      example: {
        request: {
          cnpj: '12345678000195'
        },
        response: {
          isValid: true,
          formatted: '12.345.678/0001-95'
        }
      }
    },
    // Conteúdo
    {
      category: 'content',
      method: 'POST',
      endpoint: '/api/content/text-formatter',
      title: 'Formatação de Texto',
      description: 'Aplica diferentes tipos de formatação ao texto',
      requestBody: {
        text: 'string',
        format: 'uppercase|lowercase|capitalize|title|camelCase|snake_case|kebab-case'
      },
      response: {
        original: 'string',
        formatted: 'string',
        format: 'string'
      },
      example: {
        request: {
          text: 'hello world',
          format: 'uppercase'
        },
        response: {
          original: 'hello world',
          formatted: 'HELLO WORLD',
          format: 'uppercase'
        }
      }
    },
    {
      category: 'content',
      method: 'POST',
      endpoint: '/api/content/text-counter',
      title: 'Análise de Texto',
      description: 'Analisa estatísticas detalhadas de um texto',
      requestBody: {
        text: 'string'
      },
      response: {
        characters: 'number',
        words: 'number',
        sentences: 'number',
        paragraphs: 'number',
        readingTime: 'number',
        wordFrequency: 'object'
      },
      example: {
        request: {
          text: 'Este é um texto de exemplo para análise.'
        },
        response: {
          characters: 42,
          words: 8,
          sentences: 1,
          paragraphs: 1,
          readingTime: 0.4,
          wordFrequency: {
            'é': 1,
            'um': 1,
            'texto': 1,
            'de': 1,
            'exemplo': 1,
            'para': 1,
            'análise': 1
          }
        }
      }
    },
    // Domínios
    {
      category: 'domains',
      method: 'POST',
      endpoint: '/api/domains/dns-checker',
      title: 'Consulta DNS',
      description: 'Consulta registros DNS de um domínio',
      requestBody: {
        domain: 'string',
        type: 'A|AAAA|CNAME|MX|TXT|NS'
      },
      response: {
        domain: 'string',
        type: 'string',
        records: 'array'
      },
      example: {
        request: {
          domain: 'example.com',
          type: 'A'
        },
        response: {
          domain: 'example.com',
          type: 'A',
          records: [
            {
              name: 'example.com',
              type: 'A',
              value: '93.184.216.34',
              ttl: 300
            }
          ]
        }
      }
    },
    {
      category: 'domains',
      method: 'POST',
      endpoint: '/api/domains/redirect-checker',
      title: 'Verificação de Redirecionamento',
      description: 'Rastreia redirecionamentos de uma URL',
      requestBody: {
        url: 'string'
      },
      response: {
        originalUrl: 'string',
        finalUrl: 'string',
        redirects: 'array',
        statusCode: 'number'
      },
      example: {
        request: {
          url: 'http://example.com'
        },
        response: {
          originalUrl: 'http://example.com',
          finalUrl: 'https://example.com',
          redirects: [
            {
              from: 'http://example.com',
              to: 'https://example.com',
              statusCode: 301
            }
          ],
          statusCode: 200
        }
      }
    },
    // Imagens
    {
      category: 'images',
      method: 'POST',
      endpoint: '/api/images/convert-format',
      title: 'Processamento de Imagens',
      description: 'Processa imagens (converter, redimensionar, comprimir)',
      requestBody: {
        image: 'file',
        operation: 'convert|resize|compress',
        options: 'object'
      },
      response: {
        processed: 'boolean',
        originalSize: 'number',
        newSize: 'number',
        format: 'string',
        url: 'string'
      },
      example: {
        request: {
          operation: 'resize',
          options: {
            width: 800,
            height: 600,
            quality: 85
          }
        },
        response: {
          processed: true,
          originalSize: 1024000,
          newSize: 512000,
          format: 'jpeg',
          url: '/uploads/processed-image.jpg'
        }
      }
    },
    {
      category: 'images',
      method: 'POST',
      endpoint: '/api/images/qr-generator',
      title: 'Gerador de QR Code',
      description: 'Gera QR Codes em PNG (base64) a partir de texto ou URL',
      requestBody: {
        text: 'string',
        size: 'number (opcional, padrão 200)',
        color: 'string (opcional, padrão #000000)',
        backgroundColor: 'string (opcional, padrão #FFFFFF)'
      },
      response: {
        dataUrl: 'string (data:image/png;base64,...)',
        size: 'number',
        color: 'string',
        backgroundColor: 'string',
        timestamp: 'string'
      },
      example: {
        request: {
          text: 'https://nservers.com',
          size: 200
        },
        response: {
          dataUrl: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAA...',
          size: 200,
          color: '#000000',
          backgroundColor: '#FFFFFF',
          timestamp: '2024-05-01T12:00:00.000Z'
        }
      }
    },
    // Utils
    {
      category: 'utils',
      method: 'POST',
      endpoint: '/api/utils/mac-lookup',
      title: 'Consulta de MAC Address',
      description: 'Consulta fabricante a partir de um endereço MAC',
      requestBody: {
        mac: 'string'
      },
      response: {
        mac: 'string',
        vendor: 'string'
      },
      example: {
        request: { mac: '00:1A:2B:3C:4D:5E' },
        response: { mac: '00:1A:2B:3C:4D:5E', vendor: 'Cisco Systems' }
      }
    },
    {
      category: 'utils',
      method: 'POST',
      endpoint: '/api/utils/mac-generator',
      title: 'Gerador de MAC Address',
      description: 'Gera endereços MAC aleatórios',
      requestBody: {},
      response: { mac: 'string' },
      example: {
        request: {},
        response: { mac: 'A1:B2:C3:D4:E5:F6' }
      }
    },
    {
      category: 'utils',
      method: 'POST',
      endpoint: '/api/utils/iban-validator',
      title: 'Validador de IBAN',
      description: 'Valida e extrai informações de um IBAN',
      requestBody: { iban: 'string' },
      response: { valid: 'boolean', country: 'string', bankCode: 'string', accountNumber: 'string', formatted: 'string' },
      example: {
        request: { iban: 'DE89 3704 0044 0532 0130 00' },
        response: { valid: true, country: 'Alemanha', bankCode: '37040044', accountNumber: '0532013000', formatted: 'DE89 3704 0044 0532 0130 00' }
      }
    },
    // Texto
    {
      category: 'text',
      method: 'POST',
      endpoint: '/api/text/numeronym-generator',
      title: 'Gerador de Numerônimos',
      description: 'Gera numerônimos (ex: internationalization → i18n)',
      requestBody: { text: 'string' },
      response: { numeronym: 'string' },
      example: {
        request: { text: 'internationalization' },
        response: { numeronym: 'i18n' }
      }
    },
    {
      category: 'text',
      method: 'POST',
      endpoint: '/api/text/ascii-art-generator',
      title: 'Gerador de ASCII Art',
      description: 'Gera arte em ASCII a partir de texto',
      requestBody: { text: 'string' },
      response: { asciiArt: 'string' },
      example: {
        request: { text: 'nServers' },
        response: { asciiArt: ' _   _                 \n| \ | | _____      __ \n|  \| |/ _ \\ \ / /\n| |\  |  __/>  < \n|_| \_|\___/_/\_\\n' }
      }
    },
    {
      category: 'text',
      method: 'POST',
      endpoint: '/api/text/string-obfuscator',
      title: 'Ofuscador de String',
      description: 'Ofusca textos embaralhando caracteres',
      requestBody: { text: 'string' },
      response: { obfuscated: 'string' },
      example: {
        request: { text: 'segredo' },
        response: { obfuscated: 's*e*g*r*e*d*o' }
      }
    },
    // Desenvolvimento
    {
      category: 'development',
      method: 'POST',
      endpoint: '/api/development/sql-generator',
      title: 'Geração de SQL',
      description: 'Gera queries SQL a partir de descrições em linguagem natural',
      requestBody: {
        description: 'string',
        dialect: 'mysql|postgresql|sqlite'
      },
      response: {
        query: 'string',
        dialect: 'string',
        explanation: 'string'
      },
      example: {
        request: {
          description: 'Selecionar todos os usuários com idade maior que 18',
          dialect: 'mysql'
        },
        response: {
          query: 'SELECT * FROM users WHERE age > 18;',
          dialect: 'mysql',
          explanation: 'Query seleciona todos os registros da tabela users onde a idade é maior que 18'
        }
      }
    },
    {
      category: 'development',
      method: 'POST',
      endpoint: '/api/development/css-generator',
      title: 'Geração de CSS',
      description: 'Gera estilos CSS a partir de descrições em linguagem natural',
      requestBody: {
        description: 'string'
      },
      response: {
        css: 'string'
      },
      example: {
        request: {
          description: 'Estilo para um botão'
        },
        response: {
          css: 'button { background-color: #4CAF50; color: white; padding: 10px 20px; text-align: center; text-decoration: none; display: inline-block; font-size: 16px; margin: 4px 2px; cursor: pointer; border-radius: 12px; }'
        }
      }
    },
    {
      category: 'development',
      method: 'POST',
      endpoint: '/api/development/benchmark-builder',
      title: 'Construtor de Benchmark',
      description: 'Executa benchmarks de código JavaScript',
      requestBody: { code: 'string', iterations: 'number' },
      response: { totalTime: 'number', averageTime: 'number', iterations: 'number', opsPerSecond: 'number', memory: 'number' },
      example: {
        request: { code: 'for(let i=0;i<1000;i++){}', iterations: 1000 },
        response: { totalTime: 12, averageTime: 0.012, iterations: 1000, opsPerSecond: 83333, memory: 5.2 }
      }
    },
    {
      category: 'utils',
      method: 'GET',
      endpoint: '/api/utils/user-agent-finder',
      title: 'User Agent Finder',
      description: 'Descobre o user agent do visitante.',
      requestBody: {},
      response: { userAgent: 'string' },
      example: {
        request: {},
        response: { userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64)...' }
      }
    },
    {
      category: 'utils',
      method: 'GET',
      endpoint: '/api/utils/my-ip',
      title: 'Qual é Meu IP?',
      description: 'Descobre o IP público do visitante.',
      requestBody: {},
      response: { ip: 'string' },
      example: {
        request: {},
        response: { ip: '189.123.45.67' }
      }
    },
    {
      category: 'utils',
      method: 'POST',
      endpoint: '/api/utils/ping',
      title: 'Ping',
      description: 'Meça o ping para qualquer endereço (host ou IP).',
      requestBody: { address: 'string' },
      response: { host: 'string', alive: 'boolean', time: 'number', output: 'string' },
      example: {
        request: { address: 'google.com' },
        response: { host: 'google.com', alive: true, time: 23.1, output: 'PING google.com (142.250.190.14): 56 data bytes\n64 bytes from 142.250.190.14: icmp_seq=0 ttl=115 time=23.1 ms' }
      }
    },
    {
      category: 'utils',
      method: 'POST',
      endpoint: '/api/utils/unshorten-url',
      title: 'Achar URL Encurtada',
      description: 'Descobre a URL original de links encurtados (bit.ly, tinyurl, etc).',
      requestBody: { url: 'string' },
      response: { original: 'string', unshortened: 'string' },
      example: {
        request: { url: 'https://bit.ly/exemplo' },
        response: { original: 'https://bit.ly/exemplo', unshortened: 'https://sitefinal.com/alguma-coisa' }
      }
    }
  ];

  const copyToClipboard = async (text, endpoint) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedEndpoint(endpoint);
      setTimeout(() => setCopiedEndpoint(null), 2000);
    } catch (err) {
      console.error('Erro ao copiar:', err);
    }
  };

  const filteredEndpoints = apiEndpoints.filter(endpoint => {
    const matchesSearch = endpoint.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         endpoint.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         endpoint.endpoint.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || endpoint.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const getMethodColor = (method) => {
    switch (method) {
      case 'GET': return 'bg-green-100 text-green-800 border-green-200';
      case 'POST': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'PUT': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'DELETE': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const handleTestClick = (endpoint) => {
    setTestStates((prev) => ({
      ...prev,
      [endpoint]: {
        open: !prev[endpoint]?.open,
        request: prev[endpoint]?.request || JSON.stringify(apiEndpoints.find(e => e.endpoint === endpoint).example.request, null, 2),
        response: null,
        loading: false,
        error: ''
      }
    }));
  };

  const handleRequestChange = (endpoint, value) => {
    setTestStates((prev) => ({
      ...prev,
      [endpoint]: {
        ...prev[endpoint],
        request: value
      }
    }));
  };

  const handleSendRequest = async (endpoint, method) => {
    setTestStates((prev) => ({
      ...prev,
      [endpoint]: {
        ...prev[endpoint],
        loading: true,
        error: '',
        response: null
      }
    }));
    try {
      const reqBody = JSON.parse(testStates[endpoint].request);
      const data = await apiPost(endpoint.replace('/api', ''), reqBody);
      setTestStates((prev) => ({
        ...prev,
        [endpoint]: {
          ...prev[endpoint],
          loading: false,
          response: data
        }
      }));
    } catch (err) {
      setTestStates((prev) => ({
        ...prev,
        [endpoint]: {
          ...prev[endpoint],
          loading: false,
          error: err.message || 'Erro ao enviar requisição'
        }
      }));
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
              <div className="mb-8">
        <div className="flex items-center space-x-3 mb-4">
          <div className="p-2 bg-blue-100 rounded-lg">
            <BookOpen className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Documentação da API</h1>
            <p className="text-gray-600">Guia completo de todas as rotas disponíveis</p>
          </div>
        </div>
        
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200">
          <div className="flex items-start space-x-4">
            <Zap className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-blue-900 mb-2">Base URL</h3>
              <code className="bg-white px-3 py-2 rounded border text-sm font-mono">
                {config.getApiUrl()}
              </code>
              <p className="text-sm text-blue-700 mt-2">
                Todas as requisições devem incluir o header <code className="bg-blue-100 px-1 rounded">Content-Type: application/json</code>
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="mb-8 space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Buscar endpoints..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div className="flex flex-wrap gap-2">
          {categories.map((category) => {
            const Icon = category.icon;
            return (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg border transition-all ${
                  selectedCategory === category.id
                    ? 'bg-blue-100 border-blue-300 text-blue-700'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Icon className={`w-4 h-4 ${category.color}`} />
                <span>{category.name}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="space-y-6">
        {filteredEndpoints.map((ep, idx) => (
          <motion.div
            key={ep.endpoint + '-' + ep.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.1 }}
            className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow"
          >
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getMethodColor(ep.method)}`}>
                      {ep.method}
                    </span>
                    <code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                      {ep.endpoint}
                    </code>
                    <button
                      onClick={() => copyToClipboard(ep.endpoint, ep.endpoint)}
                      className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {copiedEndpoint === ep.endpoint ? (
                        <Check className="w-4 h-4 text-green-500" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                  <h3 className="text-lg font-semibold mb-1">
                    {ep.title}
                  </h3>
                  <p className="text-gray-600">
                    {ep.description}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-6">
              <div>
                <h4 className="font-semibold mb-3">Request Body</h4>
                <div className="rounded-lg p-5 bg-gray-200 text-gray-900 border border-gray-300 font-mono text-base overflow-x-auto">
                  <pre>
                    <code>{JSON.stringify(ep.requestBody, null, 2)}</code>
                  </pre>
                </div>
              </div>

              <div>
                <h4 className="font-semibold mb-3">Response</h4>
                <div className="rounded-lg p-5 bg-gray-200 text-gray-900 border border-gray-300 font-mono text-base overflow-x-auto">
                  <pre>
                    <code>{JSON.stringify(ep.response, null, 2)}</code>
                  </pre>
                </div>
              </div>

              <div>
                <h4 className="font-semibold mb-3">Exemplo</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h5 className="text-sm font-medium mb-2">Request</h5>
                    <div className="bg-gray-200 text-gray-900 border border-gray-300 rounded-lg p-5 font-mono text-base overflow-x-auto">
                      <pre>
                        <code>{JSON.stringify(ep.example.request, null, 2)}</code>
                      </pre>
                    </div>
                  </div>
                  <div>
                    <h5 className="text-sm font-medium mb-2">Response</h5>
                    <div className="bg-gray-200 text-gray-900 border border-gray-300 rounded-lg p-5 font-mono text-base overflow-x-auto">
                      <pre>
                        <code>{JSON.stringify(ep.example.response, null, 2)}</code>
                      </pre>
                    </div>
                  </div>
                </div>
              </div>

              {ep.method === 'POST' && (
                <div className="pt-4 border-t border-gray-200">
                  <button
                    className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    onClick={() => handleTestClick(ep.endpoint)}
                  >
                    <ExternalLink className="w-4 h-4" />
                    <span>Testar Endpoint</span>
                  </button>
                </div>
              )}

              {testStates[ep.endpoint]?.open && (
                <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <div className="mb-2 font-semibold">Request Body</div>
                  <textarea
                    className="w-full font-mono text-base p-2 border border-gray-300 rounded mb-2 text-[#000000] bg-white"
                    rows={6}
                    value={testStates[ep.endpoint].request}
                    onChange={e => handleRequestChange(ep.endpoint, e.target.value)}
                  />
                  <button
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                    onClick={() => handleSendRequest(ep.endpoint, ep.method)}
                    disabled={testStates[ep.endpoint].loading}
                  >
                    {testStates[ep.endpoint].loading ? 'Enviando...' : 'Enviar'}
                  </button>
                  {testStates[ep.endpoint].error && (
                    <div className="mt-2 text-red-600">{testStates[ep.endpoint].error}</div>
                  )}
                  {testStates[ep.endpoint].response && (
                    <div className="mt-4">
                      <div className="mb-2 font-semibold">Response</div>
                      <pre className="bg-gray-200 p-3 rounded text-[#000000] font-mono text-base overflow-x-auto">
                        {JSON.stringify(testStates[ep.endpoint].response, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        ))}
      </div>

      {filteredEndpoints.length === 0 && (
        <div className="text-center py-12">
          <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2">
            Nenhum endpoint encontrado
          </h3>
          <p className="text-gray-600">
            Tente ajustar os filtros de busca ou categoria
          </p>
        </div>
      )}
    </div>
  );
};

export default ApiDocs; 